'use client'

import React from 'react'

import { FilterAccordionSection } from '@/components/FilterAccordionSection'
import { FaqAccordion } from '@/components/Landing/FaqAccordion'
import type { FaqSectionData } from '@/lib/faqs/faqData'
import { useMessages } from '@/lib/i18n/useMessages'

const FAQ_SECTION_INITIAL_VISIBLE_COUNT = 5

const FAQ_ILLUSTRATION = '/media/homepage/faq/faq-parrot.webp'
const CONTACT_BRAND_MARK = '/media/contact/brand-mark.svg'

export type FAQSectionProps = {
  faqData: FaqSectionData
  intro?: readonly string[]
  variant?: 'default' | 'contact'
}

export const FAQSection: React.FC<FAQSectionProps> = ({
  faqData,
  intro,
  variant = 'default',
}) => {
  const messages = useMessages()
  const copy = messages.landing.faq
  const contactFaq = messages.contact.faq
  const isContactVariant = variant === 'contact'

  if (faqData.items.length === 0) {
    return null
  }

  const sidebar = isContactVariant ? (
    <>
      <img
        alt=""
        aria-hidden
        className="h-20 w-auto self-start"
        height={80}
        loading="lazy"
        src={CONTACT_BRAND_MARK}
        width={125}
      />
      <h2
        className="mt-4 font-display text-[1.75rem] font-medium leading-8 tracking-[-0.04em] text-dark-800/80"
        id="faq-heading"
      >
        {contactFaq.title}
      </h2>
      <p className="mt-4 whitespace-pre-line font-outfit text-sm leading-5 text-dark-800/80">
        {contactFaq.legalBody}
      </p>
      <img
        alt=""
        aria-hidden
        className="mt-6 hidden h-auto w-full max-w-[26rem] lg:block"
        height={418}
        loading="lazy"
        src={FAQ_ILLUSTRATION}
        width={418}
      />
    </>
  ) : (
    <>
      <h2 id="faq-heading" className="text-title text-bluetext lg:leading-none">
        {copy.title}
      </h2>

      <div className="mt-10 lg:mt-12">
        <p className="font-outfit text-sm font-bold leading-5 text-dark-800/80">{copy.helpTitle}</p>
        <p className="mt-1 font-outfit text-sm leading-5 text-dark-800/80">{copy.helpBody}</p>
        <p className="mt-1 font-outfit text-sm leading-5 text-dark-800/80">
          <a
            className="inline-flex min-h-6 items-center underline-offset-2 hover:underline"
            href={`tel:${copy.phoneHref}`}
          >
            {copy.phone}
          </a>
        </p>
        <p className="font-outfit text-sm leading-5 text-dark-800/80">
          <a
            className="inline-flex min-h-6 items-center underline-offset-2 hover:underline"
            href={`mailto:${copy.email}`}
          >
            {copy.email}
          </a>
        </p>
      </div>

      <img
        alt=""
        aria-hidden
        className="mt-8 hidden h-auto w-full max-w-[26rem] lg:block"
        height={418}
        loading="lazy"
        src={FAQ_ILLUSTRATION}
        width={418}
      />
    </>
  )

  return (
    <FilterAccordionSection
      ariaLabelledBy="faq-heading"
      gridSpacing={isContactVariant ? 'compact' : 'default'}
      mobileIllustration={{
        height: 418,
        src: FAQ_ILLUSTRATION,
        width: 418,
      }}
      sidebar={sidebar}
    >
      <FaqAccordion
        faqData={faqData}
        initialVisibleCount={FAQ_SECTION_INITIAL_VISIBLE_COUNT}
        intro={intro}
      />
    </FilterAccordionSection>
  )
}
